import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "../helpers";

const POINTS = [
  "A creative digital & AI partner, not just a vendor",
  "Product-minded engineers who care about outcomes",
  "Transparent process, honest timelines, real support",
];

const K = "text-brand-orange";      // keyword
const S = "text-[#4AF0FF]";         // string
const F = "text-white";             // function / identifier
const C = "text-brand-muted";       // comment
const D = "text-brand-sec";         // default / punctuation

// meaningful code, revealed line-by-line on scroll
const LINES = [
  { indent: 0, tokens: [[K, "import"], [D, " { "], [F, "Paramarsh"], [D, " } "], [K, "from"], [S, ' "@paramarsh/core"'], [D, ";"]] },
  { indent: 0, tokens: [] },
  { indent: 0, tokens: [[K, "const"], [F, " agency"], [D, " = "], [K, "new"], [F, " Paramarsh"], [D, "({"]] },
  { indent: 1, tokens: [[F, "focus"], [D, ": ["], [S, '"web"'], [D, ", "], [S, '"mobile"'], [D, ", "], [S, '"ai"'], [D, "],"]] },
  { indent: 1, tokens: [[F, "values"], [D, ": ["], [S, '"design"'], [D, ", "], [S, '"scale"'], [D, ", "], [S, '"speed"'], [D, "],"]] },
  { indent: 0, tokens: [[D, "});"]] },
  { indent: 0, tokens: [] },
  { indent: 0, tokens: [[K, "export async"], [K, " function"], [F, " build"], [D, "("], [F, "idea"], [D, ") {"]] },
  { indent: 1, tokens: [[K, "const"], [F, " product"], [D, " = "], [K, "await"], [F, " agency"], [D, "."], [F, "craft"], [D, "("], [F, "idea"], [D, ");"]] },
  { indent: 1, tokens: [[F, "product"], [D, "."], [F, "optimize"], [D, "({ "], [F, "perf"], [D, ": "], [S, "99"], [D, ", "], [F, "ux"], [D, ": "], [S, '"premium"'], [D, " });"]] },
  { indent: 1, tokens: [[K, "return"], [F, " product"], [D, "."], [F, "launch"], [D, "();"], [C, "  // → a better tomorrow"]] },
  { indent: 0, tokens: [[D, "}"]] },
];

function CodeLine({ progress, start, span, line, index }) {
  const opacity = useTransform(progress, [start, start + span], [0, 1]);
  const x = useTransform(progress, [start, start + span], [-14, 0]);
  return (
    <motion.div style={{ opacity, x }} className="flex items-start gap-2 sm:gap-3 leading-6">
      <span className="select-none font-mono text-[10px] sm:text-[11px] text-white/20 w-4 sm:w-5 text-right shrink-0">{index + 1}</span>
      <code className="font-mono text-[11px] sm:text-[12.5px] md:text-[13px] whitespace-pre" style={{ paddingLeft: line.indent * 12 }}>
        {line.tokens.length === 0 ? " " : line.tokens.map((t, i) => (
          <span key={i} className={t[0]}>{t[1]}</span>
        ))}
      </code>
    </motion.div>
  );
}

function ScrollCode() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 85%", "center 40%"] });

  const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const pctText = useTransform(scrollYProgress, (v) => `${Math.round(v * 100)}%`);
  const scanTop = useTransform(scrollYProgress, [0, 1], ["6%", "94%"]);
  const scanOpacity = useTransform(scrollYProgress, [0, 0.05, 0.95, 1], [0, 1, 1, 0]);
  const glow = useTransform(scrollYProgress, [0, 1], [0.15, 0.5]);

  const seg = 1 / LINES.length;

  return (
    <div
      ref={ref}
      data-testid="about-visual"
      className="relative rounded-3xl overflow-hidden gradient-border border border-white/10 h-[380px] sm:h-[460px] bg-[#08080B]"
    >
      {/* ambient grid + glow */}
      <div className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: "linear-gradient(rgba(255,138,0,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,138,0,0.5) 1px,transparent 1px)", backgroundSize: "30px 30px" }} />
      <motion.div style={{ opacity: glow }} className="absolute -right-16 -top-16 w-64 h-64 rounded-full blur-[90px]">
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle,#FF8A00,transparent 70%)" }} />
      </motion.div>

      {/* floating tech tokens */}
      <div className="absolute top-14 right-6 z-20 font-mono text-brand-orange/70 text-lg animate-float" style={{ animationDelay: "0s" }}>{"</>"}</div>
      <div className="absolute bottom-24 right-10 z-20 font-mono text-brand-accent/60 text-base animate-float" style={{ animationDelay: "1.1s" }}>{"{ }"}</div>
      <div className="absolute top-1/2 right-4 z-20 rounded-full glass border border-white/10 px-2.5 py-1 font-mono text-[10px] text-brand-sec animate-float" style={{ animationDelay: "0.6s" }}>AI</div>

      {/* editor window */}
      <div className="relative z-10 h-full flex flex-col">
        {/* title bar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06]">
          <span className="h-3 w-3 rounded-full bg-[#FF5F5F]" />
          <span className="h-3 w-3 rounded-full bg-[#FFC400]" />
          <span className="h-3 w-3 rounded-full bg-[#4AF0FF]" />
          <span className="ml-3 font-mono text-[11px] text-brand-muted">paramarsh.ts</span>
          <span className="ml-auto flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse" />
            <span className="font-mono text-[10px] text-brand-muted">building</span>
          </span>
        </div>

        {/* code body */}
        <div className="relative flex-1 px-3 sm:px-4 py-4 overflow-x-auto overflow-y-hidden scrollbar-thin">
          {/* scan line */}
          <motion.div style={{ top: scanTop, opacity: scanOpacity }} className="absolute left-0 right-0 h-[26px] -translate-y-1/2 pointer-events-none">
            <div className="h-full w-full" style={{ background: "linear-gradient(90deg,transparent,rgba(255,138,0,0.10),transparent)" }} />
          </motion.div>

          <div className="relative space-y-0.5">
            {LINES.map((line, i) => (
              <CodeLine key={i} progress={scrollYProgress} start={i * seg * 0.85} span={seg} line={line} index={i} />
            ))}
            {/* blinking cursor */}
            <div className="flex items-center gap-3 leading-6">
              <span className="w-5 shrink-0" />
              <span className="inline-block h-4 w-[8px] bg-brand-orange animate-pulse" />
            </div>
          </div>
        </div>

        {/* compile progress */}
        <div className="px-5 py-3 border-t border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] text-brand-muted uppercase tracking-widest">Compiling product</span>
            <motion.span className="font-mono text-[10px] text-brand-orange">{pctText}</motion.span>
          </div>
          <div className="h-1 w-full rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div style={{ width: barWidth }} className="h-full rounded-full" >
              <div className="h-full w-full" style={{ background: "linear-gradient(to right,#FF5F00,#FFC400)", boxShadow: "0 0 10px #FF8A00" }} />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <section id="about" className="relative py-28 md:py-32 bg-[#0B0B0E] border-y border-white/5 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,138,0,0.4) 1px,transparent 1px),linear-gradient(90deg,rgba(255,138,0,0.4) 1px,transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative">
        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] text-brand-orange uppercase mb-4">/ About</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              We turn ambitious ideas into <span className="text-gradient-orange">intelligent products</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 font-body text-brand-sec leading-relaxed max-w-xl">
              Paramarsh Tech is a creative digital agency helping startups and enterprises design,
              build and automate world-class digital experiences. We blend elegant design,
              robust engineering and applied AI to deliver solutions that move businesses forward.
            </p>
          </Reveal>
          <div className="mt-8 space-y-4">
            {POINTS.map((p, i) => (
              <Reveal key={p} delay={0.2 + i * 0.08}>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-orange mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="font-body text-brand-sec">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.2}>
          <ScrollCode />
        </Reveal>
      </div>
    </section>
  );
}
